import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import Markdown from "@/components/Markdown";
import { Bold, Italic, Heading2, List, Eye, PencilLine } from "lucide-react";

/**
 * A minimal, warm Write/Preview editor that lets sharers add headings, bullets,
 * bold/italic, and links using plain markdown. Zero-config, keyboard-friendly.
 */
export default function ContextEditor({ value, onChange, placeholder = "" }) {
  const [tab, setTab] = useState("write");

  const wrap = (before, after = before, sample = "text") => {
    const el = document.getElementById("context-textarea");
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end) || sample;
    const next = value.slice(0, start) + before + selected + after + value.slice(end);
    onChange(next);
    // restore selection roughly
    setTimeout(() => {
      el.focus();
      el.selectionStart = start + before.length;
      el.selectionEnd = start + before.length + selected.length;
    }, 0);
  };

  const prefixLines = (prefix) => {
    const el = document.getElementById("context-textarea");
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end) || "item";
    const lines = selected.split("\n").map((l) => (l ? `${prefix}${l}` : l)).join("\n");
    const next = value.slice(0, start) + lines + value.slice(end);
    onChange(next);
    setTimeout(() => {
      el.focus();
      el.selectionStart = start;
      el.selectionEnd = start + lines.length;
    }, 0);
  };

  const btnCls = "p-2 rounded-full text-[#2A2823]/60 hover:text-[#2A2823] hover:bg-[#B79256]/12 transition-colors";

  return (
    <div className="rounded-2xl border border-[#B79256]/40 bg-[#FBF8F1] overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#B79256]/25 bg-white/50">
        <div className="flex items-center gap-1" role="toolbar" aria-label="Formatting">
          <button type="button" data-testid="ctx-bold" onClick={() => wrap("**")} className={btnCls} title="Bold"><Bold className="w-4 h-4" strokeWidth={1.75}/></button>
          <button type="button" data-testid="ctx-italic" onClick={() => wrap("_")} className={btnCls} title="Italic"><Italic className="w-4 h-4" strokeWidth={1.75}/></button>
          <button type="button" data-testid="ctx-h2" onClick={() => prefixLines("## ")} className={btnCls} title="Heading"><Heading2 className="w-4 h-4" strokeWidth={1.75}/></button>
          <button type="button" data-testid="ctx-list" onClick={() => prefixLines("- ")} className={btnCls} title="Bullet list"><List className="w-4 h-4" strokeWidth={1.75}/></button>
        </div>
        <div className="inline-flex rounded-full border border-[#B79256]/30 p-0.5 bg-white">
          <button
            type="button"
            data-testid="ctx-tab-write"
            onClick={() => setTab("write")}
            className={`accent-label px-3 py-1 rounded-full inline-flex items-center gap-1.5 transition-colors ${tab === "write" ? "bg-[#4E5A3D] text-[#FBF8F1]" : "text-[#2A2823]/60 hover:text-[#2A2823]"}`}
          >
            <PencilLine className="w-3.5 h-3.5" strokeWidth={1.75}/> Write
          </button>
          <button
            type="button"
            data-testid="ctx-tab-preview"
            onClick={() => setTab("preview")}
            className={`accent-label px-3 py-1 rounded-full inline-flex items-center gap-1.5 transition-colors ${tab === "preview" ? "bg-[#4E5A3D] text-[#FBF8F1]" : "text-[#2A2823]/60 hover:text-[#2A2823]"}`}
          >
            <Eye className="w-3.5 h-3.5" strokeWidth={1.75}/> Preview
          </button>
        </div>
      </div>

      {tab === "write" ? (
        <Textarea
          id="context-textarea"
          data-testid="context-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={7}
          className="rounded-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 resize-y min-h-[180px]"
        />
      ) : (
        <div data-testid="context-preview" className="p-5 min-h-[180px]">
          {value.trim() ? (
            <Markdown>{value}</Markdown>
          ) : (
            <p className="text-[#2A2823]/40 italic">Nothing to preview yet.</p>
          )}
        </div>
      )}

      <div className="px-4 py-2 border-t border-[#B79256]/25 bg-white/40">
        <p className="text-xs text-[#2A2823]/50">
          Tip — supports <b>**bold**</b>, <em>_italics_</em>, <code>## headings</code>, and <code>- bullets</code>. Keep it kind and specific.
        </p>
      </div>
    </div>
  );
}
